import type { Component, Entity2D } from '../../odie';

import {
  BlockId,
  blockPropsMap,
  type DirectionId,
  type SodaBlockProps,
} from '../defs/block';
import { config } from '../defs/config';
import { blockOptionFromCountDirection } from '../util/blockOption';
import { SodaBlockView } from '../views/blocks/SodaBlockView';
import { SodaColor } from '../views/blocks/components/SodaBottle';

// types
//-----------------------------------------------------------------------------
export type SodaBlockComponentOptions = {
  props: SodaBlockProps;
  option?: number;
};

/*
  Soda block component
*/
export class SodaBlockComponent implements Component {
  public static readonly NAME = 'blockSoda';
  public entity!: Entity2D;
  public view!: SodaBlockView;
  private _counter!: number;
  /** Hit points is a temporary hit counter, used for selecting targets before they are actually hit */
  private _hitPoints!: number;
  private _color!: SodaColor;
  private _direction!: DirectionId;
  private options!: SodaBlockComponentOptions;

  public init(options: SodaBlockComponentOptions): void {
    this.view = new SodaBlockView({ ...options.props }, this.getMaxCount());
    this.options = options;
    this._color = options.props.color as SodaColor;
    this.counter = options.props.count;
    this.hitPoints = this.counter;
    this._direction = options.props.direction;
    this.entity.c.block.getGameplayState = () => this.getGameplayState();
  }

  private getMaxCount() {
    const id = this.entity.c.block.blockId as BlockId;
    const baseProps = blockPropsMap[id] as SodaBlockProps;
    return baseProps.count;
  }

  public get counter(): number {
    return this._counter;
  }

  public set counter(value: number) {
    value = Math.max(0, value);
    this._counter = value;
  }

  public get hitPoints() {
    return this._hitPoints;
  }

  public set hitPoints(value: number) {
    value = Math.max(0, value);
    this._hitPoints = value;
  }

  public get color(): SodaColor {
    return this._color;
  }

  public get direction(): DirectionId {
    return this._direction;
  }

  public get props(): SodaBlockProps {
    return this.options.props;
  }

  public getGameplayState() {
    return blockOptionFromCountDirection({
      count: this._counter,
      direction: this._direction,
    });
  }

  public getBottomOffsetX(targetScale = 1) {
    const size = config.tile.size;
    const sizeHalf = size / 2;
    const offset = size * targetScale;
    const offsetHalf = offset * 0.5;

    switch (this.direction) {
      case 'up':
        return sizeHalf - offsetHalf;
      case 'down':
        return sizeHalf - offsetHalf;
      case 'left':
        return size * 2 - offset;
      case 'right':
      default:
        return 0;
    }
  }

  public getBottomOffsetY(targetScale = 1) {
    const size = config.tile.size;
    const sizeHalf = size / 2;
    const offset = size * targetScale;
    const offsetHalf = offset * 0.5;

    switch (this.direction) {
      case 'up':
        return size * 2 - offset;
      case 'left':
        return sizeHalf - offsetHalf;
      case 'right':
        return sizeHalf - offsetHalf;
      case 'down':
      default:
        return 0;
    }
  }
}
